/**
 * The web side of a module (RULES §A-2): `web/src/modules/<key>/module.ts` exports
 * `{ key, routes, publicRoutes, nav, setup }`. Routes hang under the app route (signed in, in
 * the shell); public routes hang under the public route (no sign-in, no shell — capability-link
 * pages); nav items declare the permission (and server module) that gates them; `setup`
 * registers entity tabs, realtime invalidations and entity links.
 */
import type { AnyRoute } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import type { Me } from '@/lib/api/types';
import type { PermissionCheck } from '@/lib/can';

export type NavSection = 'main' | 'operate' | 'improve' | 'manage';

export interface NavChild {
  key: string;
  /** English source string, marked with msg(). */
  label: string;
  to: string;
  permission?: PermissionCheck;
  /** Path prefixes that make this child active (default: `to`). */
  match?: string[];
}

export interface NavItem {
  /** Destination key, as used in `/api/me.persona.nav`. */
  key: string;
  /** Other persona keys that mean this destination (e.g. 'devices' → Fleet). */
  aliases?: string[];
  label: string;
  /** One-line purpose (shown on "coming next" pages and in search). */
  description?: string;
  icon: LucideIcon;
  to: string;
  section: NavSection;
  /** Default order (IA §2) for destinations the persona does not rank. */
  order: number;
  permission?: PermissionCheck;
  /** Server module that must be enabled for the company (RULES §A-2). */
  module?: string;
  match?: string[];
  children?: NavChild[];
  /**
   * Show only when this holds for the signed-in user — a fact about the account, never a role
   * check (e.g. Me pages: the user is linked to a driver). Optional; absent = always.
   */
  when?: (me: Me) => boolean;
}

export interface ModuleDef<R extends readonly AnyRoute[] = readonly AnyRoute[], P extends readonly AnyRoute[] = readonly AnyRoute[]> {
  key: string;
  /** Pages in the app: `createRoute({ getParentRoute: () => appRoute, … })`. */
  routes: R;
  /**
   * Pages outside the app shell that need no sign-in (a shared report, a TV screen, a message
   * opened by link): `createRoute({ getParentRoute: () => publicRoute, … })` from
   * `@/app/publicRoute`. They may call only public endpoints.
   */
  publicRoutes?: P;
  nav: NavItem[];
  setup?: () => void;
}

/** Keeps the route tuples' types so the router stays fully typed. */
export function defineModule<const R extends readonly AnyRoute[], const P extends readonly AnyRoute[] = readonly []>(
  def: ModuleDef<R, P>,
): ModuleDef<R, P> & { publicRoutes: P } {
  return { ...def, publicRoutes: def.publicRoutes ?? ([] as readonly AnyRoute[] as P) };
}
